import React, { useState } from 'react';
import './NordstromTimeline.css';

const NordstromTimeline = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [inputPassword, setInputPassword] = useState('');
    const [error, setError] = useState(false);
    const [activeTab, setActiveTab] = useState('timeline');
    const [expandedPhases, setExpandedPhases] = useState({});

    const handleLogin = (e) => {
        if (e) e.preventDefault();
        if (inputPassword.toLowerCase() === 'guru') {
            setIsAuthenticated(true);
            setError(false);
        } else {
            setError(true);
            setInputPassword('');
        }
    };

    const togglePhase = (index) => {
        setExpandedPhases(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const expandAll = () => {
        const all = {};
        timelineData.forEach((_, i) => all[i] = true);
        setExpandedPhases(all);
    };

    const collapseAll = () => {
        setExpandedPhases({});
    };

    const timelineData = [
        {
            date: "May 11, 2021 - May 2022",
            title: "TPM Excellence & Rapid Promotion",
            phase: "Phase 1: High-Velocity Growth",
            description: "Joined Nordstrom and achieved promotion within 1 year due to exceptional delivery of core technical programs and standard-setting leadership.",
            points: [
                "<strong>4-Digit Store Expansion</strong>: Managed e2e as lead TPM, coordinating 20+ teams and mitigating critical risks for 3-digit store reuse.",
                "<strong>RMS SKU ID Project</strong>: Established the communication and reporting architecture adopted as the organizational standard.",
                "<strong>Bias for Action</strong>: Championed 'One-Way vs Two-Way Door' decision making to unblock engineering teams and accelerate delivery.",
                "<strong>Reputation</strong>: Rapidly built a 'Great Name' within the TPM organization as a go-to leader for ambiguous, high-stakes infrastructure projects."
            ],
            status: "success",
            quickInsight: "Achieved promotion within 1 year through exceptional delivery."
        },
        {
            date: "May 2022 - Oct 2023",
            title: "Leadership & Team Stewardship",
            phase: "Phase 2: Org & Process Stability",
            description: "Stepped in to manage day-to-day operations for three critical engineering teams (Org, Vendor, Supplier Report) during a period of leadership transition.",
            points: [
                "<strong>Stewardship</strong>: Provided essential continuity and mentorship to team members following a large-scale re-org and leadership resignations.",
                "<strong>Knowledge Transfer</strong>: Acted as the primary KT lead for incoming Engineering Managers and Directors across the three domains.",
                "<strong>Operational Savings</strong>: Delivered $35k+ in projected annual AWS cost optimizations through EBS deprecation and strategic re-platforming.",
                "<strong>Project Success</strong>: Successfully launched Curbside Pickup integration, AEDW Re-platforming, and high-scale Anniversary support."
            ],
            status: "success",
            quickInsight: "Led 3 teams concurrently during critical leadership turnover."
        },
        {
            date: "Oct 2023 - Aug 2024",
            title: "The Sidelined PM Phase",
            phase: "Phase 3: Structural Disadvantage",
            description: "Transitioned from a TPM to a PM role but was assigned a fragmented portfolio of legacy applications for **sustain and production support only**, with zero opportunity for feature development.",
            points: [
                "<strong>Sustain-Only Mandate</strong>: Assigned all legacy planning applications without any feature development; just asked to sustain and handle production support, which intentionally hindered learning.",
                "<strong>Impossible Breadth</strong>: Managed Merch Financial Planning (Oracle), plus a suite of Quantrix apps (Price Type, Location, Assortment, and Supplier Category Planning) with minimal strategic visibility.",
                "<strong>Voice of Employee</strong>: Formally raised concerns multiple times that this breadth was unscalable and hindered my learning, but was insisted: 'Since it is only sustain, you can handle it.'",
                "<strong>Professional Stunting</strong>: Despite a 23-year track record (Amazon/Amex), was assigned to reactive firefighting, effectively being 'put to fail' through lack of enhancement ownership."
            ],
            status: "stress",
            quickInsight: "Assigned 10+ legacy apps for 'sustain only'; ignored warnings about unscalable breadth."
        },
        {
            date: "Aug 2024 - June 2025",
            title: "Heroic Execution & Technical Scale",
            phase: "Phase 4: High-Impact Turnaround",
            description: "Stepped into the 'Spend Control Hard Launch' and simultaneously led a massive infrastructure migration, proving senior PdM scale and technical depth.",
            points: [
                "<strong>Spend Control Hard Launch</strong>: Drove this high-stakes project to a successful rollout, enabling Nordstrom to control over <strong>$90M of annual overspending</strong> per year.",
                "<strong>GCP Migration Initiative</strong>: Successfully managed the migration of 10+ Legacy applications and 100s of tables/reports from Teradata to GCP, coordinating with Google/GCP and Quantrix teams.",
                "<strong>Operational Automation</strong>: Engineered and automated multiple manual processes, including <strong>SPE file extraction</strong> and <strong>Handr Control Monthly reporting</strong> for Business leaders.",
                "<strong>Stakeholder Management</strong>: Handled all executive and partner communications (internal and external) during intense launch windows, proving Senior PM capability."
            ],
            status: "success",
            quickInsight: "Led $90M Spend Control launch & 10+ legacy app GCP migration."
        },
        {
            date: "June 2025 - Sep 26, 2025",
            title: "Performance Excellence (H1 Connect)",
            phase: "Phase 5: Performance Recognition",
            description: "Managed the high-complexity Toolio Migration / Legacy Sync initiative, culminating in an **outstanding H1 Performance Review** on Sep 26, 2025.",
            points: [
                "<strong>H1 Connect Success</strong>: Received a top-tier performance review on **Sep 26, 2025**, validating my contributions as a high-performing Product Manager (4.9 years tenure).",
                "<strong>Established Track Record</strong>: Consistently demonstrated an ability to turn around messy, inherited projects into successful deliveries with zero strategic oversight.",
                "<strong>Metric Alignment</strong>: Validated as a strategic asset with the competency to lead large-scale, high-ROI initiatives independently."
            ],
            status: "success",
            quickInsight: "Received top-tier 'Sep 26 H1 Connect' review; validated as high-performing."
        },
        {
            date: "Oct 2025 - Dec 2025",
            title: "Selfless Crisis Support (Post-VISA Trip)",
            phase: "Phase 6: Rescue & Ground Truth",
            description: "Immediately upon returning from critical VISA-related travel to India, selflessly stepped in to support a **failing Toolio project** (which I was not leading) to ensure organizational stability.",
            points: [
                "<strong>Crisis Salvage</strong>: Self-assigned to 500+ inherited UAT issues in a project led by others, operating at a <strong>'Machine'</strong> level to prevent total project failure.",
                "<strong>Search for Ground Truth</strong>: In high-stakes triage where syncs were failing, utilized manual deep-dive verification ('Reading numbers aloud') to ensure 100% data integrity.",
                "<strong>Resilience</strong>: Functioned as the primary <strong>'Resolver'</strong> during the most critical 3-month window in Toolio history, despite extreme personal stress and minimal PM mentorship."
            ],
            status: "high-stress",
            quickInsight: "Selflessly rescued project I was not leading; resolved 500+ UAT issues."
        },
        {
            date: "Jan 9, 2026",
            title: "PIP & Targeted Bonus Denial",
            phase: "Phase 7: Procedural Inconsistency",
            description: "Management delivered a PIP on Jan 9. Despite explicit verbal confirmation of **bonus eligibility (Jan 31st employment)** from **Laura Duve**, the bonus was subsequently denied.",
            points: [
                "<strong>Conflicting Information</strong>: Leadership (Laura Duve) confirmed eligibility based on the Jan 31st cutoff; however, the subsequent denial contradicts this organizational directive.",
                "<strong>Suspicious Timing</strong>: PIP delivery effectively blocks the **$89K Performance Bonus** payout, despite a top-tier track record for 4.9 years and a 'Great' review just 3 months prior.",
                "<strong>Strategic Counterpoint</strong>: This PIP serves as a tactical barrier to a deserved **$89K payout**, ignoring the heroic Nov-Dec support role provided to the Toolio team.",
                "<strong>Family Crisis</strong>: The PIP was issued during a documented family health crisis, necessitating an FMLA transition on Jan 12."
            ],
            status: "critical",
            quickInsight: "Conflicting bonus directives: Laura Duve (Eligible) vs. Kirsten (Denied)."
        },
        {
            date: "April 2026 - Present",
            title: "Strategic Reset: Location Planning",
            phase: "Phase 8: High-Impact Discovery",
            description: "Transitioned to **Location Planning**, a greenfield strategic product initiative, following return from FMLA. Committed to delivering 100% excellence in this new domain.",
            points: [
                "<strong>Greenfield Discovery</strong>: Engaging on high-impact strategic product discovery for Location Planning, leveraging 23 years of scale-ready PM expertise.",
                "<strong>Full Commitment</strong>: Actively engaged in weekly sessions with clear progress updates and a bias for strategic delivery.",
                "<strong>Strategic Value</strong>: Utilizing this opportunity to showcase senior-level skillset in a domain that values strategic innovation over maintenance firefighting."
            ],
            status: "success",
            quickInsight: "Actively leading greenfield strategic discovery in Location Planning."
        }
    ];

    const renderTimeline = () => (
        <div className="tab-content-wrapper">
            <div className="timeline-nav">
                <button className="btn-outline" onClick={expandAll}>Expand All Details</button>
                <button className="btn-outline" onClick={collapseAll}>Collapse All</button>
            </div>
            <main className="timeline-wrapper">
                <div className="timeline-line"></div>
                {timelineData.map((item, index) => (
                    <div key={index} className="timeline-event">
                        <div className={`timeline-card ${item.status} ${expandedPhases[index] ? 'expanded' : ''}`}
                             onClick={() => togglePhase(index)}>
                            <div className="event-dot"></div>
                            <div className="card-header">
                                <span className="event-date">{item.date}</span>
                                <span className="phase-tag">{item.phase}</span>
                            </div>
                            <h3>{item.title}</h3>
                            {!expandedPhases[index] && (
                                <p className="quick-insight">
                                    <span>✨</span> {item.quickInsight || item.description.substring(0, 80) + '...'}
                                </p>
                            )}
                            {expandedPhases[index] && (
                                <div className="expanded-content">
                                    <p dangerouslySetInnerHTML={{ __html: item.description }} />
                                    <ul className="points-list">
                                        {item.points.map((point, i) => (
                                            <li key={i} dangerouslySetInnerHTML={{ __html: point }} />
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </main>
        </div>
    );

    const renderConcerns = () => (
        <div className="tab-content-wrapper">
            <div className="content-card">
                <h2>Structural Impediments to Growth</h2>
                <div className="concern-grid">
                    <div className="concern-item">
                        <h4>The Forced PdM Transition</h4>
                        <p>In June/July 2023, the company mandated all TPMs transition to PdM. As a 23-year veteran, I embraced the change, but the shift was not met with the <strong>strategic mentoring</strong> required to master the nuances of the new role.</p>
                    </div>
                    <div className="concern-item">
                        <h4>Impossible Portfolio Breadth</h4>
                        <p>Managing Oracle Merch Financial Planning AND four distinct Quantrix applications, plus the Supplier Profitability suite, created a <strong>"Complexity Ceiling."</strong> This scope is vastly beyond a single-PM mandate.</p>
                    </div>
                    <div className="concern-item">
                        <h4>Stunted Learning Curve</h4>
                        <p>True PdM development occurs during feature creation. Being assigned all legacy planning applications for **sustain/production support only** directly hindered my learning and professional growth.</p>
                    </div>
                    <div className="concern-item">
                        <h4>Management Blindspot</h4>
                        <p>I proactively raised these structural concerns multiple times. I was insisted that <strong>"since it is only sustain, you can handle it,"</strong> ignoring the strategic necessity of a balanced portfolio for senior-level growth.</p>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: '40px', textAlign: 'center' }}>
                <button className="btn-primary" onClick={() => setActiveTab('counterpoint')}>Review the Performance Counter-Case</button>
            </div>
        </div>
    );

    const renderCounterpoint = () => (
        <div className="tab-content-wrapper">
            <div className="content-card">
                <h2>Counterpoint Analysis (PIP Rebuttal)</h2>
                <div className="rebuttal-section">
                    <p className="card-intro">
                        The following analysis provides the necessary structural context for specific performance 
                        observations cited on Jan 9, 2026. This data serves to align expectations with the 
                        operational realities of the legacy portfolio.
                    </p>
                    <div className="rebuttal-table">
                        <div className="rebuttal-row header">
                            <span>Performance Observation (PIP Citations)</span>
                            <span>Structural Context & Counter-Analysis</span>
                        </div>
                        <div className="rebuttal-row">
                            <span className="allegation">
                                <strong>Process Formalization & Triage</strong><br/>
                                <em>"Lack of daily triage call ownership; missing Kanban updates; 15+ legacy tickets daily during blackouts."</em>
                            </span>
                            <span className="reality">
                                I managed <strong>15+ high-priority Toolio issues daily</strong> (on top of 10+ legacy applications) to ensure system stability. Furthermore, <strong>I successfully established the triage process in parallel</strong>; the claim of 'missing updates' ignores the tactical necessity of resolving blockers first in an emergency launch window while simultaneously setting up the operational framework.
                            </span>
                        </div>
                        <div className="rebuttal-row">
                            <span className="allegation">
                                <strong>Deadline Variance (Dec 29)</strong><br/>
                                <em>"Failed to meet Dec 29 deadline; insufficient navigation of project ambiguity."</em>
                            </span>
                            <span className="reality">
                                The Dec 29 deadline was a <strong>manufactured constraint</strong>: assigned on Dec 23 (one day before Christmas) to deliver complex <strong>Open to Buy (OTB)</strong> requirements during a holiday blackout. My strategic priority remained on the <strong>500+ inherited UAT issues</strong> to ensure Ground Truth for the broader launch.
                            </span>
                        </div>
                        <div className="rebuttal-row">
                            <span className="allegation">
                                <strong>Bonus Eligibility Controversy</strong><br/>
                                <em>"PIP resulted in no base pay increase or bonus this cycle."</em>
                            </span>
                            <span className="reality">
                                I was explicitly told by **Laura Duve** following our Jan 9 meeting that I am **eligible for the bonus** as I was employed on Jan 31st. The current denial is inconsistent with this leadership directive and ignores a career-long track record of excellence.
                            </span>
                        </div>
                        <div className="rebuttal-row">
                            <span className="allegation">
                                <strong>Remote Status & Equity</strong><br/>
                                <em>"Concerns raised in H2 connect regarding remote equity."</em>
                            </span>
                            <span className="reality">
                                My remote status has never been a barrier to my <strong>300+ UAT resolutions</strong> or my Tier-1 migration successes. I am fully committed to the <strong>Location Planning</strong> initiative and am ready for relocations/office presence to ensure maximum strategic alignment.
                            </span>
                        </div>
                        <div className="rebuttal-row highlight">
                            <span className="allegation">
                                <strong>Subject Matter Expertise</strong><br/>
                                <em>"Requires deeper technical/functional SME in Toolio configuration and legacy sync."</em>
                            </span>
                            <span className="reality">
                                I am the <strong>Primary Resolver</strong> for the most complex legacy syncs. My 23-year career (Amazon/Amex) and my successful leadership of the **GCP Migration (10+ apps, 100s of tables/reports)** prove my technical mastery; any perceived "SME gap" is a result of a <strong>"Sustain-Only" mandate</strong> that intentionally precluded feature-level learning.
                            </span>
                        </div>
                        <div className="rebuttal-row highlight">
                            <span className="allegation">
                                <strong>Adaptive Autonomy</strong><br/>
                                <em>"Requires high-level direction; frequent check-ins needed to ensure progress."</em>
                            </span>
                            <span className="reality">
                                My <strong>H1 Connect review</strong> explicitly praised my ability to handle ambiguity. The recent influx of "frequent check-ins" is a reactive management style (micro-oversight) that has created a barrier to the senior-level velocity I have demonstrated for over two decades.
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: '40px', textAlign: 'center' }}>
                <button className="btn-primary" onClick={() => setActiveTab('ask')}>Final Ask & Realignment</button>
            </div>
        </div>
    );

    const renderAsk = () => (
        <div className="tab-content-wrapper">
            <div className="content-card ask-card">
                <h2>The Case for Strategic Realignment</h2>
                <p className="card-intro">
                    With over <strong>23 years of IT leadership</strong> at institutions like <strong>Amazon, Amex, and Nordstrom</strong>, 
                    I have high confidence in my ability to deliver in my <strong>Product Management role</strong> if assigned a focused, 
                    well-resourced initiative similar to my senior peers.
                </p>
                <ul className="ask-list">
                    <li>
                        <span className="ask-icon">🎯</span>
                        <div className="ask-text">
                            <h4>Location Planning Excellence</h4>
                            <p>I am fully engaged in the **Location Planning discovery work**. I request a realignment of success metrics to focus on this strategic, greenfield milestone where my PM maturity can drive measurable value.</p>
                        </div>
                    </li>
                    <li>
                        <span className="ask-icon">🤝</span>
                        <div className="ask-text">
                            <h4>Partnership & Autonomy</h4>
                            <p>A return to a collaborative partnership where performance is evaluated on strategic throughput, moving away from the tactical micro-oversight of the legacy portfolio.</p>
                        </div>
                    </li>
                </ul>

                <div className="relocation-commitment" style={{ marginTop: '30px', background: '#1e293b', border: '1px solid #334155' }}>
                    <div className="relocation-badge" style={{ background: '#3b82f6' }}>👨‍👩‍👧 Human Capital & Personal Context</div>
                    <p style={{ textAlign: 'left', color: '#cbd5e1', fontSize: '0.95rem', lineHeight: '1.6' }}>
                        I want to continue my job with <strong>Nordstrom</strong>. I have high confidence in my value to the organization 
                        and humbly request that I am not pushed out during this transition. 🎯
                    </p>
                    <p style={{ textAlign: 'left', color: '#cbd5e1', fontSize: '0.95rem', lineHeight: '1.6', marginTop: '15px' }}>
                        I am currently on an <strong>H1B Visa</strong>, and the current job market for H1B sponsorship is historically challenging. A transition away from Nordstrom would not only be a career setback but would create an immediate <strong>60-day deadline</strong> for me to leave the country. Most critically, this would <strong>separate my family</strong>, as my daughter is starting college here and will not be returning to India with us.
                    </p>
                    <p style={{ textAlign: 'left', color: '#cbd5e1', fontSize: '0.95rem', lineHeight: '1.6', marginTop: '15px' }}>
                        Following a <strong>top-tier Performance Review on Sep 26, 2025</strong> and my heroic rescue efforts in Nov-Dec, I am humbly requesting that my <strong>deserved $89K performance bonus</strong> be paid as scheduled. These funds are essential for me to fulfill my commitment to my daughter's college education and ensure her academic stability during this transition.
                    </p>
                </div>

                <div className="rebuttal-section" style={{ marginTop: '40px' }}>
                    <h3 style={{ color: '#f8fafc', marginBottom: '20px' }}>Institutional Innovation & Proof (Maker Portfolio)</h3>
                    <div className="maker-grid">
                        <div className="maker-card">
                            <span className="experience-callout">Product Innovation</span>
                            <h4><span className="ask-icon">🌐</span> Savanna United</h4>
                            <p>Independently architected and launched a community platform for HOA leadership transition, demonstrating zero-to-one product delivery.</p>
                        </div>
                        <div className="maker-card">
                            <span className="experience-callout">AI Integration</span>
                            <h4><span className="ask-icon">🧮</span> Math Guru</h4>
                            <p>Built an AI-driven adaptive learning platform that personifies educational excellence through automated curriculum generation.</p>
                        </div>
                        <div className="maker-card">
                            <span className="experience-callout">Language Tech</span>
                            <h4><span className="symbol">🗣️</span> Vaak App</h4>
                            <p>Developed a high-fidelity language learning solution leveraging AI voice synthesis and modern React/Node architecture.</p>
                        </div>
                        <div className="maker-card">
                            <span className="experience-callout">Global Leadership</span>
                            <h4><span className="ask-icon">🏛️</span> Amazon, Amex & Nordstrom</h4>
                            <p>Led complex migrations and modernizations for Tier-1 systems, proving management scale with technical precision.</p>
                        </div>
                    </div>
                </div>

                <div className="relocation-commitment">
                    <div className="relocation-badge">⚡ Professional Commitment</div>
                    <h3>Ready for Full In-Office Presence</h3>
                    <p style={{ color: '#94a3b8', maxWidth: '600px', margin: '15px auto 0' }}>
                        I am fully prepared to <strong>relocate immediately</strong> and work directly from the office 
                        to ensure maximum collaboration and leadership visibility for my next initiative.
                    </p>
                </div>
            </div>
        </div>
    );

    if (!isAuthenticated) {
        return (
            <div className="login-overlay">
                <div className="login-card">
                    <span className="login-icon">🔒</span>
                    <h2>Secure Access</h2>
                    <p>Please enter the password to view the Nordstrom Career Case.</p>
                    <form className="login-form" onSubmit={handleLogin}>
                        <input 
                            type="password" 
                            className="login-input" 
                            placeholder="Password"
                            value={inputPassword}
                            onChange={(e) => setInputPassword(e.target.value)}
                            autoFocus
                        />
                        {error && <div className="login-error">Incorrect password. Please try again.</div>}
                        <button type="submit" className="btn-primary">Unlock Dashboard</button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="nordstrom-timeline-container">
            <div className="timeline-hero">
                <div className="hero-main">
                    <h1>Nordstrom Career Case</h1>
                    <p className="subtitle">Synthesizing Success, Identifying Barriers, and Defining the Path Forward</p>
                </div>
                <div className="hero-stats">
                    <div className="stat-item">
                        <span className="stat-value">23+ Years</span>
                        <span className="stat-label">Global IT Leadership</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">4.9 Years</span>
                        <span className="stat-label">Nordstrom Tenure</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">&lt; 1 Year</span>
                        <span className="stat-label">Promotion Speed</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">3 Teams</span>
                        <span className="stat-label">Led Concurrently</span>
                    </div>
                </div>
            </div>

            <nav className="tabs-container">
                <button 
                    className={`tab-btn ${activeTab === 'timeline' ? 'active' : ''}`}
                    onClick={() => setActiveTab('timeline')}
                >
                    1. Proven Track Record
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'concerns' ? 'active' : ''}`}
                    onClick={() => setActiveTab('concerns')}
                >
                    2. Structural Concerns
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'counterpoint' ? 'active' : ''}`}
                    onClick={() => setActiveTab('counterpoint')}
                >
                    3. Counterpoint Analysis
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'ask' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ask')}
                >
                    4. The Strategic Ask
                </button>
            </nav>

            {activeTab === 'timeline' && renderTimeline()}
            {activeTab === 'concerns' && renderConcerns()}
            {activeTab === 'counterpoint' && renderCounterpoint()}
            {activeTab === 'ask' && renderAsk()}

            <section className="timeline-case">
                <h2>The Takeaway</h2>
                <p>
                    I am a high-output leader who thrives on complex technical delivery. 
                    The recent challenges are the result of an impossible portfolio breadths 
                    and a forced transition without support. I am ready to return to delivering 
                    standard-setting results if aligned with the right strategic scope.
                </p>
                <div style={{ marginTop: '30px' }}>
                    <a href="/" className="btn-primary">Back to Portfolio</a>
                </div>
            </section>
        </div>
    );
};

export default NordstromTimeline;
